import { useState, useEffect, useCallback, useRef } from 'react';
import apiService, { EvaluationJob } from '../services/api';

interface UseJobPollingResult {
  job: EvaluationJob | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Custom React hook for polling evaluation job status
 *
 * Automatically polls the backend for job updates at the specified interval.
 * Stops polling when the job reaches a terminal state (completed, failed, cancelled).
 * Cleans up on unmount to prevent memory leaks.
 *
 * @param jobId - The ID of the job to poll
 * @param interval - Polling interval in milliseconds (default: 2000ms)
 * @param enabled - Whether polling is enabled (default: true)
 * @returns Object containing job data, loading state, error state, and refetch function
 */
export const useJobPolling = (
  jobId: string,
  interval: number = 2000,
  enabled: boolean = true
): UseJobPollingResult => {
  const [job, setJob] = useState<EvaluationJob | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Use ref to store timeout ID so we can clear it
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Use ref to track if component is mounted
  const isMountedRef = useRef<boolean>(true);

  /**
   * Fetch job status from the API
   */
  const fetchJob = useCallback(async () => {
    if (!jobId || !enabled) return;

    try {
      const data = await apiService.getEvaluation(jobId);

      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setJob(data);
        setError(null);
        setLoading(false);

        // Continue polling if job is still in progress
        const isInProgress = data.status === 'pending' || data.status === 'running';
        if (isInProgress) {
          timeoutRef.current = setTimeout(fetchJob, interval);
        }
      }
    } catch (err) {
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to fetch job');
        setLoading(false);

        // Retry after interval even on error (might be temporary)
        timeoutRef.current = setTimeout(fetchJob, interval);
      }
    }
  }, [jobId, interval, enabled]);

  /**
   * Manually trigger a refetch of job status
   */
  const refetch = useCallback(async () => {
    setLoading(true);
    await fetchJob();
  }, [fetchJob]);

  // Effect to start polling
  useEffect(() => {
    isMountedRef.current = true;

    // Start fetching immediately
    fetchJob();

    // Cleanup function
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [fetchJob]);

  return { job, loading, error, refetch };
};

export default useJobPolling;
